import * as THREE from "three";
import { TNT } from "../world/blocks";
import type { AtlasBundle } from "../world/atlas";
import { createBlockBufferGeometry } from "./block-geometry";
import type { PrimedTnt } from "../entities/tnt";

/**
 * 3D Renderer for Primed TNT entities.
 *
 * Each primed TNT block uses the authentic textured unit cube geometry with
 * directional face shading, white flash blinking, and subtle swelling as the
 * fuse burns down.
 */

interface TntVisual {
  readonly group: THREE.Group;
  readonly mesh: THREE.Mesh;
  readonly material: THREE.MeshBasicMaterial;
}

const WHITE = new THREE.Color(0xffffff);

export class PrimedTntRenderer {
  readonly group = new THREE.Group();

  private readonly geometry: THREE.BufferGeometry;
  private readonly visuals = new Map<number, TntVisual>();
  private readonly atlasTexture: THREE.Texture;

  constructor(scene: THREE.Scene, atlas: AtlasBundle) {
    this.group.name = "primed-tnt";
    scene.add(this.group);

    this.atlasTexture = atlas.texture;
    this.geometry = createBlockBufferGeometry(TNT, 1);
  }

  /**
   * Sync visual meshes with the primed TNT simulation state.
   */
  sync(tnts: readonly PrimedTnt[], light: number): void {
    const seen = new Set<number>();
    const brightness = Math.max(0, light);

    for (const tnt of tnts) {
      seen.add(tnt.id);
      let visual = this.visuals.get(tnt.id);
      if (!visual) {
        visual = this.createVisual();
        this.visuals.set(tnt.id, visual);
        this.group.add(visual.group);
      }

      // Center of 1x1x1 unit cube is 0.5 above feet level
      visual.group.position.set(
        tnt.position.x,
        tnt.position.y + 0.5,
        tnt.position.z,
      );

      // Flashing white at ~2.5 Hz, accelerating to 5 Hz in the final second
      const flashFrequency = tnt.fuse < 1.0 ? 10 : 5;
      const isWhiteFlash = Math.floor(tnt.fuse * flashFrequency) % 2 === 0;

      // Subtle expansion swell as detonation approaches
      const swellProgress = Math.max(0, 1.0 - tnt.fuse / tnt.maxFuse);
      const swellScale = 1.0 + swellProgress * 0.08;
      visual.mesh.scale.set(swellScale, swellScale, swellScale);

      // Color tint & flash
      visual.material.color.setScalar(brightness);
      if (isWhiteFlash) {
        visual.material.color.lerp(WHITE, 0.92);
      }
    }

    // Cleanup removed TNT entities
    for (const [id, visual] of this.visuals) {
      if (seen.has(id)) continue;
      visual.group.removeFromParent();
      visual.material.dispose();
      this.visuals.delete(id);
    }
  }

  dispose(): void {
    for (const visual of this.visuals.values()) {
      visual.group.removeFromParent();
      visual.material.dispose();
    }
    this.visuals.clear();
    this.geometry.dispose();
    this.group.removeFromParent();
  }

  private createVisual(): TntVisual {
    const group = new THREE.Group();
    const material = new THREE.MeshBasicMaterial({
      map: this.atlasTexture,
      vertexColors: true,
      fog: true,
    });
    const mesh = new THREE.Mesh(this.geometry, material);
    group.add(mesh);

    return { group, mesh, material };
  }
}
